import XLSX from "xlsx";
import i18n from "i18next";
import moment from "moment";

import { formatDate, formatPlannedDate } from "./dateHelpers";
import { formatDimension, formatQuantityValue } from "./unitOfMeasurementHelpers";
import { formatShipmentStatus } from "../helpers/shipmentStatusHelpers";
import { formatVehicle } from "./vehicleHelpers";
import { saveAs } from "./fileHelpers";
import StatusTypes from "../components/PurchaseOrder/StatusTypes";

const save = (isXLSX, workBook, legalFileName) => {
  if (isXLSX) {
    return saveAs(XLSX.write(workBook, { type: "array" }), legalFileName, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
  }

  return saveAs(XLSX.write(workBook, { bookType: "csv", type: "array" }), legalFileName, "text/plain");
};

export const exportOrders = (customer, purchaseOrders, prePlannings, dateDisplayFormat, isXLSX = false) => {
  const formattedPO = purchaseOrders.map(po => {
    const { soldTo = {}, shipTo = {}, mills = [] } = po;
    const [firstMill = {}] = mills;
    const { location = {} } = firstMill;
    return {
      [i18n.t("purchaseOrder.number")]: po.customerPOId,
      [i18n.t("purchaseOrder.mill")]: location.locationId,
      [i18n.t("purchaseOrder.soldTo")]: soldTo.customerName,
      [i18n.t("purchaseOrder.shipTo")]: shipTo.customerName,
      [i18n.t("purchaseOrder.endUser")]: po.endUserId,
      [i18n.t("purchaseOrder.poPeriod")]: po.pricingPeriodDate,
      [i18n.t("purchaseOrder.ordered")]: formatQuantityValue(po.quantityAcknowledged),
      [i18n.t("purchaseOrder.shipped")]: formatQuantityValue(po.quantityShipped),
      [i18n.t("purchaseOrder.remaining")]: formatQuantityValue(po.quantityRemaining)
    };
  });

  const formattedPrePlanning = prePlannings.map(planning => {
    const { mill = {}, orderedProduct = {} } = planning;
    const isDeliveryLate = planning.deliveryStatus === StatusTypes.Late;
    const status = isDeliveryLate ? `${i18n.t("loads.status.Delayed")} ${Math.abs(planning.deliveryDays)}` : i18n.t("loads.status.OnTime");
    return {
      [i18n.t("purchaseOrder.number")]: planning.customerPOId,
      [i18n.t("purchaseOrder.endUser")]: planning.endUserId,
      [i18n.t("purchaseOrder.mill")]: mill.millId,
      [i18n.t("products.product")]: orderedProduct.productName,
      [i18n.t("products.dimensions")]: formatDimension(orderedProduct.dimension),
      [i18n.t("purchaseOrder.ordered")]: formatQuantityValue(orderedProduct.quantityAcknowledged),
      [i18n.t("purchaseOrder.delivery")]: formatDate(planning.deliveryDate, dateDisplayFormat),
      [i18n.t("purchaseOrder.pressDate")]: formatDate(planning.pressDate, dateDisplayFormat),
      [i18n.t("purchaseOrder.status")]: status
    };
  });

  const currentDate = moment(new Date()).format("YYYY-MM-DD_HH-mm-ss");
  const filename = `${i18n.t("purchaseOrder.orders")}__${customer.customerName}__${currentDate}.${isXLSX ? "xlsx" : "csv"}`;
  const workBook = XLSX.utils.book_new();
  const workSheet = XLSX.utils.json_to_sheet([{ [i18n.t("purchaseOrder.orders")]: customer.customerName }]);

  XLSX.utils.sheet_add_json(workSheet, formattedPO, { origin: 3 });
  XLSX.utils.sheet_add_json(workSheet, [{}], { origin: -1 });
  XLSX.utils.sheet_add_json(workSheet, formattedPrePlanning, { origin: -1 });
  XLSX.utils.book_append_sheet(workBook, workSheet, i18n.t("purchaseOrder.orders"));

  return save(isXLSX, workBook, filename.replace(/([^a-zA-Z0-9._ ]+)/gi, "-"));
};

export const exportOrder = (purchaseOrder, products, orders, dateDisplayFormat, isXLSX = false) => {
  const formattedProducts = products.map(product => {
    const { planningStatus = {}, productionStatus = {}, shipmentStatus = {} } = product;
    return {
      [i18n.t("products.product")]: product.productName,
      [i18n.t("products.dimensions")]: formatDimension(product.dimension),
      [i18n.t("products.planning")]: formatQuantityValue(planningStatus.quantity),
      [i18n.t("products.produced")]: formatQuantityValue(productionStatus.quantity),
      [i18n.t("products.shipped")]: formatQuantityValue(shipmentStatus.quantity),
      [i18n.t("products.marks")]: String(product.marks),
      [i18n.t("products.coreDescription")]: String(product.coreDescription)
    };
  });

  const formattedOrders = orders.map(order => {
    const { loadStatus = {}, quantityProduced = {}, quantityShipped = {} } = order;
    return {
      [i18n.t("loads.load")]: order.loadId,
      [i18n.t("loads.deliveryStatus")]: loadStatus.statusValue,
      [i18n.t("loads.planning")]: formatPlannedDate(order.quantityPlanned, order, { dateDisplayFormat }),
      [i18n.t("loads.produced")]: formatQuantityValue(quantityProduced.quantity),
      [i18n.t("loads.shipped")]: formatQuantityValue(quantityShipped.quantity),
      [i18n.t("loads.shipmentStatus")]: formatShipmentStatus(order.loadShipmentStatus, dateDisplayFormat)
    };
  });

  const currentDate = moment(new Date()).format("YYYY-MM-DD_HH-mm-ss");
  const filename = `${i18n.t("purchaseOrder.order")}__${purchaseOrder.customerPOId}__${currentDate}.${isXLSX ? "xlsx" : "csv"}`;
  const { soldTo = {}, shipTo = {}, careOf = {} } = purchaseOrder;

  const workBook = XLSX.utils.book_new();
  const workSheet = XLSX.utils.json_to_sheet([
    {
      [i18n.t("purchaseOrder.order")]: purchaseOrder.customerPOId,
      [i18n.t("purchaseOrder.soldTo")]: soldTo.customerName,
      [i18n.t("purchaseOrder.shipTo")]: shipTo.customerName,
      [i18n.t("purchaseOrder.poPeriod")]: purchaseOrder.pricingPeriodDate,
      [i18n.t("purchaseOrder.ordered")]: formatQuantityValue(purchaseOrder.quantityAcknowledged),
      [i18n.t("purchaseOrder.shipped")]: formatQuantityValue(purchaseOrder.quantityShipped),
      [i18n.t("purchaseOrder.remaining")]: formatQuantityValue(purchaseOrder.quantityRemaining),
      [i18n.t("purchaseOrder.endUser")]: purchaseOrder.endUserId,
      [i18n.t("purchaseOrder.careOf")]: careOf.locationName
    }
  ]);

  XLSX.utils.sheet_add_json(workSheet, formattedProducts, { origin: 3 });
  XLSX.utils.sheet_add_json(workSheet, [], { origin: -1 });
  XLSX.utils.sheet_add_json(workSheet, formattedOrders, { origin: -1 });
  XLSX.utils.book_append_sheet(workBook, workSheet, i18n.t("purchaseOrder.order"));

  return save(isXLSX, workBook, filename.replace(/([^a-zA-Z0-9._ ]+)/gi, "-"));
};

export const exportRolls = (rolls, billOfLading, selectedRollKeyBy, dateDisplayFormat, isXLSX = false) => {
  const { soldTo = {}, careOf = {}, ultimateConsignee = {}, millOfOrigin = {} } = billOfLading;
  const formattedRolls = rolls.map(roll => {
    return {
      [i18n.t(`rolls.${selectedRollKeyBy}`)]: roll[selectedRollKeyBy],
      [i18n.t("rolls.product")]: roll.productName,
      [i18n.t("rolls.dimensions")]: formatDimension(roll.dimension),
      [i18n.t("rolls.shipped")]: formatQuantityValue(roll.grossWeight),
      [i18n.t("rolls.length")]: formatQuantityValue(roll.rollLength),
      [i18n.t("rolls.surface")]: formatQuantityValue(roll.rollSurface),
      [i18n.t("rolls.rp")]: String(roll.rollsPerPack),
      [i18n.t("rolls.production")]: formatDate(roll.productionDate, dateDisplayFormat)
    };
  });

  const currentDate = moment(new Date()).format("YYYY-MM-DD_HH-mm-ss");
  const filename = `${i18n.t("rolls.rolls")}__${billOfLading.shipmentId}__${currentDate}.${isXLSX ? "xlsx" : "csv"}`;

  const workBook = XLSX.utils.book_new();
  const workSheet = XLSX.utils.json_to_sheet([
    {
      [i18n.t("loads.load")]: billOfLading.loadId,
      [i18n.t("loads.shipment")]: billOfLading.shipmentId,
      [i18n.t("billsOfLading.shippedFrom")]: millOfOrigin.locationName,
      [i18n.t("billsOfLading.careOf")]: careOf.locationName,
      [i18n.t("billsOfLading.modeOfTransport")]: billOfLading.modeOfTransport,
      [i18n.t("billsOfLading.shippedDate")]: formatDate(billOfLading.shippedDate, dateDisplayFormat),
      [i18n.t("billsOfLading.soldTo")]: soldTo.customerName,
      [i18n.t("billsOfLading.ultimateConsignee")]: ultimateConsignee.customerName,
      [i18n.t("billsOfLading.carrier")]: billOfLading.carrierName,
      [i18n.t("billsOfLading.vehicle")]: formatVehicle(billOfLading)
    }
  ]);

  XLSX.utils.sheet_add_json(workSheet, formattedRolls, { origin: 4 });
  XLSX.utils.book_append_sheet(workBook, workSheet, i18n.t("rolls.rolls"));

  return save(isXLSX, workBook, filename.replace(/([^a-zA-Z0-9._ ]+)/gi, "-"));
};



// WEBPACK FOOTER //
// ./src/helpers/xlsxHelpers.js